import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api, errMsg, input, money, pct } from './costingApi';

const DrillDialog = ({ drill, range, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!drill) return;
    setRows(null);
    api.get('/financial-dashboard/drill', { metric: drill.metric, start: range.start || undefined, end: range.end || undefined }).then(setRows).catch((e) => toast.error(errMsg(e)));
  }, [drill, range]);
  const cols = rows?.length ? Object.keys(rows[0]).filter((k) => !['id', 'lines'].includes(k) && typeof rows[0][k] !== 'object').slice(0, 9) : [];
  const fmt = (k, v) => (typeof v === 'number' && /total|amount|cost|price|value|revenue|cogs|profit|balance/.test(k) ? money(v) : String(v ?? ''));
  return (
    <Dialog open={!!drill} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl bg-slate-900 border-slate-700 text-white max-h-[88vh] overflow-y-auto" data-testid="drill-dialog">
        <DialogHeader><DialogTitle>{drill?.label} — underlying transactions ({rows?.length ?? '…'})</DialogTitle></DialogHeader>
        {rows && rows.length === 0 && <p className="text-slate-400 text-sm">No records.</p>}
        {rows?.length > 0 && (
          <div className="overflow-x-auto border border-slate-700 rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-800 text-slate-300"><tr>{cols.map((c) => <th key={c} className="p-2 text-left whitespace-nowrap">{c.replace(/_/g, ' ')}</th>)}{rows[0].lines && <th className="p-2 text-left">lines</th>}</tr></thead>
              <tbody>{rows.slice(0, 1000).map((r, i) => (
                <tr key={r.id || i} className="border-t border-slate-700">
                  {cols.map((c) => <td key={c} className="p-2 whitespace-nowrap max-w-[260px] truncate">{fmt(c, r[c])}</td>)}
                  {r.lines && <td className="p-2 whitespace-nowrap">{r.lines.map((l) => <div key={l.account + l.debit}>{l.debit ? 'Dr' : 'Cr'} {l.account_name} {money(l.debit || l.credit)}</div>)}</td>}
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Tile = ({ label, value, metric, onDrill, tone = '' }) => (
  <button type="button" disabled={!metric} onClick={() => onDrill({ metric, label })}
    className={`text-left rounded border border-slate-700 bg-slate-800/70 p-3 transition-colors ${metric ? 'hover:border-emerald-500 cursor-pointer' : 'cursor-default'}`}
    data-testid={`fd-tile-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <p className="text-[11px] text-slate-400">{label}</p><p className={`font-bold ${tone}`}>{value}</p>
  </button>
);

const Section = ({ title, children }) => (
  <div><h3 className="text-xs uppercase tracking-wide text-slate-400 mb-1">{title}</h3><div className="grid grid-cols-2 md:grid-cols-6 gap-2">{children}</div></div>
);

export const FinancialDashboard = () => {
  const [range, setRange] = useState({ start: '', end: '' });
  const [d, setD] = useState(null);
  const [drill, setDrill] = useState(null);
  const load = useCallback(() => api.get('/financial-dashboard', { start: range.start || undefined, end: range.end || undefined }).then(setD).catch((e) => toast.error(errMsg(e))), [range]);
  useEffect(() => { load(); }, [load]);
  const thisMonth = () => { const n = new Date(); setRange({ start: new Date(n.getFullYear(), n.getMonth(), 1).toISOString().slice(0, 10), end: n.toISOString().slice(0, 10) }); };
  if (!d) return <p className="text-slate-400 text-sm">Loading…</p>;
  const { sales: s, stock: k, vehicles: v, funding: f, business: b } = d;
  const tone = (n) => (n < 0 ? 'text-rose-400' : 'text-emerald-400');
  return (
    <div className="space-y-4" data-testid="financial-dashboard">
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className={`${input} w-40`} data-testid="fd-start" />
        <span className="text-slate-400">to</span>
        <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className={`${input} w-40`} data-testid="fd-end" />
        <Button size="sm" variant="outline" className="text-slate-900" onClick={thisMonth} data-testid="fd-this-month">This month</Button>
        <Button size="sm" variant="outline" className="text-slate-900" onClick={() => setRange({ start: '', end: '' })} data-testid="fd-all-time">All time</Button>
        <span className="text-xs text-slate-400">{d.range.start === '2000-01-01' ? 'All time' : d.range.start} → {d.range.end} · click any tile to see the transactions</span>
      </div>
      {s.cogs_detail?.legacy_sales_missing_cost > 0 && <p className="text-xs text-amber-400">{s.cogs_detail.legacy_sales_missing_cost} older sales ({money(s.cogs_detail.legacy_sales_missing_cost_value)}) have no cost recorded, so gross profit is overstated until the historical reconciliation (Phase 5).</p>}
      <Section title="Sales performance">
        <Tile label="Total sales" value={money(s.total_sales)} metric="sales" onDrill={setDrill} />
        <Tile label="Sales this month" value={money(s.sales_this_month)} onDrill={setDrill} />
        <Tile label="Average daily sales" value={money(s.average_daily_sales)} onDrill={setDrill} />
        <Tile label="COGS" value={money(s.cogs)} metric="cogs" onDrill={setDrill} />
        <Tile label="Gross profit" value={money(s.gross_profit)} tone={tone(s.gross_profit)} metric="missing_cost" onDrill={setDrill} />
        <Tile label="Gross margin" value={pct(s.gross_margin_pct)} onDrill={setDrill} />
      </Section>
      <Section title="Stock performance">
        <Tile label="Total acquired at cost" value={money(k.total_acquired_at_cost)} metric="vehicles" onDrill={setDrill} />
        <Tile label="Remaining at cost" value={money(k.remaining_at_cost)} metric="products_missing_cost" onDrill={setDrill} />
        <Tile label="Remaining at selling price" value={money(k.remaining_at_selling)} onDrill={setDrill} />
        <Tile label="Stock sold at cost" value={money(k.stock_sold_at_cost)} metric="cogs" onDrill={setDrill} />
        <Tile label={`Slow-moving (90d+): ${k.slow_moving_count}`} value={money(k.slow_moving_value_at_cost)} metric="slow_moving" onDrill={setDrill} />
        <Tile label="Unallocated acquisition cost" value={money(k.unallocated_acquisition_cost)} tone={k.unallocated_acquisition_cost ? 'text-rose-400' : ''} metric="vehicles" onDrill={setDrill} />
      </Section>
      <Section title="Vehicle performance">
        <Tile label="Stripping vehicles" value={v.count} metric="vehicles" onDrill={setDrill} />
        <Tile label="Vehicle acquisition cost" value={money(v.total_acquisition_cost)} metric="vehicles" onDrill={setDrill} />
        <Tile label="Revenue per vehicle" value={money(v.revenue_per_vehicle)} metric="vehicles" onDrill={setDrill} />
        <Tile label="Gross profit per vehicle" value={money(v.gross_profit_per_vehicle)} metric="vehicles" onDrill={setDrill} />
        <Tile label="Cost recovered" value={`${v.recovered_count} of ${v.count}`} metric="vehicles" onDrill={setDrill} />
        <Tile label="Most profitable" value={v.top[0] ? `${v.top[0].acq_no} ${money(v.top[0].realized_gross_profit)}` : '—'} metric="vehicles" onDrill={setDrill} />
      </Section>
      <Section title="JSM Trading funding">
        <Tile label="Total funding" value={money(f.total_advances)} metric="funding" onDrill={setDrill} />
        <Tile label="Principal repaid" value={money(f.principal_repayments)} metric="funding" onDrill={setDrill} />
        <Tile label="Outstanding funding" value={money(f.outstanding)} metric="funding" onDrill={setDrill} />
        <Tile label="Repayment %" value={pct(f.repayment_pct)} metric="funding" onDrill={setDrill} />
      </Section>
      <Section title="Business performance">
        <Tile label="Operating expenses" value={money(b.operating_expenses)} metric="expenses" onDrill={setDrill} />
        <Tile label="Operating profit" value={money(b.operating_profit)} tone={tone(b.operating_profit)} metric="expenses" onDrill={setDrill} />
        <Tile label="Stock purchases (not expensed)" value={money(b.stock_purchases_excluded)} metric="stock_excluded" onDrill={setDrill} />
        <Tile label="Net cash flow" value={money(b.net_cash_flow)} tone={tone(b.net_cash_flow)} onDrill={setDrill} />
        <Tile label="Ledger cash & bank" value={money(b.ledger_cash_and_bank)} onDrill={setDrill} />
        <Tile label="Stock investment recovery" value={pct(b.stock_investment_recovery_pct)} metric="vehicles" onDrill={setDrill} />
      </Section>
      <p className="text-[11px] text-slate-400">Cash flow = cash sales + debtor receipts + JSM advances − operating expenses − loan repayments − stock bought for cash. Ledger cash & bank only covers transactions posted by the new system.</p>
      <DrillDialog drill={drill} range={range} onClose={() => setDrill(null)} />
    </div>
  );
};
